import type { ScoreCell, ScoreMetric } from './scoreboard';
import type { TrendPayload } from './trends';

/** Pure helpers that read the raw days (score_trend_days) for the Details cards. */

export type DayIndex = Map<string, Map<string, number>>;

const DAY = 86_400_000;
const parse = (day: string) => Date.parse(`${day}T00:00:00Z`);
const format = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const addDays = (day: string, n: number) => format(parse(day) + n * DAY);

/** [metric id][day] to value, from the database's raw days. */
export function indexDays(payload: TrendPayload | undefined): DayIndex {
  const index: DayIndex = new Map();
  for (const [id, day, value] of payload?.days ?? []) {
    const map = index.get(id) ?? new Map<string, number>();
    map.set(day, Number(value));
    index.set(id, map);
  }
  return index;
}

/** The last n days ending on `end` (inclusive), oldest first. */
export function dayList(end: string, n: number, skip = 0): string[] {
  return Array.from({ length: n }, (_, i) => addDays(end, -(skip + n - 1 - i)));
}

type Defn = Pick<ScoreMetric, 'id' | 'value_type' | 'numerator_id' | 'denominator_id'>;

/** One day's number: the value itself, or numerator over denominator for a rate. */
export function dayValue(metric: Defn, index: DayIndex, day: string): number | null {
  if (metric.value_type !== 'rate') return index.get(metric.id)?.get(day) ?? null;
  const top = index.get(metric.numerator_id ?? '')?.get(day);
  const bottom = index.get(metric.denominator_id ?? '')?.get(day);
  return top === undefined || bottom === undefined || bottom === 0 ? null : top / bottom;
}

/** A window total. A rate is the sum of its numerator over the sum of its denominator, never an average. */
export function windowTotal(metric: Defn, index: DayIndex, days: string[]): ScoreCell {
  let sum = 0;
  let top = 0;
  let bottom = 0;
  let entered = 0;
  for (const day of days) {
    if (metric.value_type === 'rate') {
      const n = index.get(metric.numerator_id ?? '')?.get(day);
      const m = index.get(metric.denominator_id ?? '')?.get(day);
      if (n !== undefined && m !== undefined) {
        top += n;
        bottom += m;
        entered += 1;
      }
    } else {
      const v = index.get(metric.id)?.get(day);
      if (v !== undefined) {
        sum += v;
        entered += 1;
      }
    }
  }
  if (entered === 0) return { value: null, entered_days: 0 };
  if (metric.value_type === 'rate')
    return { value: bottom === 0 ? null : top / bottom, entered_days: entered };
  return { value: sum, entered_days: entered };
}

/** This window against the one before it. Null unless both have enough entered days. */
export function windowChange(
  metric: Defn,
  index: DayIndex,
  end: string,
  n: number,
  minDays: number,
): number | null {
  const now = windowTotal(metric, index, dayList(end, n));
  const before = windowTotal(metric, index, dayList(end, n, n));
  if (
    now.value === null ||
    before.value === null ||
    before.value === 0 ||
    now.entered_days < minDays ||
    before.entered_days < minDays
  )
    return null;
  return (now.value - before.value) / before.value;
}

/** SVG points for a small line of the last `n` days, or null with fewer than two numbers. */
export function sparkPoints(
  metric: Defn,
  index: DayIndex,
  end: string,
  n: number,
  width: number,
  height: number,
): string | null {
  const days = dayList(end, n);
  const values = days.map((day) => dayValue(metric, index, day));
  const known = values.filter((v): v is number => v !== null);
  if (known.length < 2) return null;
  const low = Math.min(...known);
  const high = Math.max(...known);
  const pad = 2;
  const x = (i: number) => pad + (i * (width - 2 * pad)) / Math.max(1, n - 1);
  const y = (v: number) =>
    high === low ? height / 2 : height - pad - ((v - low) * (height - 2 * pad)) / (high - low);
  return values
    .flatMap((v, i) => (v === null ? [] : [`${x(i).toFixed(1)},${y(v).toFixed(1)}`]))
    .join(' ');
}

/** A trailing capital tag on an item name, like "Hook A", "Video (B)" or "Variant C". */
export function variantOf(name: string): string | null {
  const match = /(?:^|\s)\(?(?:Variant\s+)?([A-D])\)?$/.exec(name.trim());
  return match?.[1] ?? null;
}

/** The tags worth a chip: only when at least two different ones appear among the items. */
export function variantTags(names: string[]): string[] {
  const tags = [
    ...new Set(names.map(variantOf).filter((tag): tag is string => tag !== null)),
  ].sort();
  return tags.length >= 2 ? tags : [];
}
